import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Users, Shield, CreditCard, AlertTriangle, CheckCircle, Gavel, MessageCircle, Heart, Copyright, Scale, Smartphone } from 'lucide-react';
import SEOHead from './SEOHead';

const linkCls = "text-rose-600 hover:text-rose-700 underline font-medium";

const TermsOfService = () => {
  const lastUpdated = "June 1, 2026";

  const sections = [
    {
      id: "account-eligibility",
      title: "1. Account & Eligibility",
      icon: <CheckCircle className="w-5 h-5" />,
      content: [
        {
          subtitle: "1.1 Eligibility",
          items: [
            "Be at least 18 years old",
            "Have the legal capacity to enter into a binding agreement",
            "Not be prohibited from using dating services under applicable law",
            "Not be a convicted sex offender or felon",
            "Not have previously had your account terminated by Shot Clock"
          ]
        },
        {
          subtitle: "1.2 Your Account",
          items: [
            "Provide accurate, current, and complete information",
            "Maintain the security of your password",
            "You are responsible for all activity under your account",
            "Notify us immediately of any unauthorized use",
            "One person, one account. You may not sell, transfer, share, or let anyone else use your account"
          ]
        },
        {
          subtitle: "1.3 Account Suspension & Termination",
          items: [
            "We may suspend or terminate your account at any time, with or without notice, if you violate these Terms or our Community Guidelines",
            "This includes posting inappropriate content (nudity, harassment, hate speech, spam), commercial solicitation, attempting to defraud users, or being reported for safety concerns",
            <>You may delete your account at any time via Settings → Account → Delete Account, or by visiting <a href="/delete-account" className={linkCls} data-testid="terms-delete-account-link">shotclockdating.com/delete-account</a></>
          ]
        }
      ]
    },
    {
      id: "the-service",
      title: "2. The Service",
      icon: <Heart className="w-5 h-5" />,
      content: [
        {
          subtitle: "What We Provide",
          items: [
            "Shot Clock Dating is a platform for adults to meet through timed video \"speed dates,\" chat with mutual matches, and optionally subscribe to premium features",
            "We provide the technology; we do not conduct background checks on users",
            "Shot Clock does not guarantee any outcome — we cannot promise you will find a match, a romantic partner, or a successful date"
          ]
        }
      ]
    },
    {
      id: "payments",
      title: "3. Payments, Subscriptions & Credits",
      icon: <CreditCard className="w-5 h-5" />,
      content: [
        {
          subtitle: "3.1 Subscriptions",
          items: [
            "Free tier: 3 speed-dating sessions per month, basic matching",
            "Premium: unlimited speed dates, AI icebreakers, advanced matching",
            "VIP: all Premium features plus personal coaching, compatibility reports, unlimited messaging",
            "Prices are shown in-app at the time of purchase",
            "Subscriptions auto-renew at the end of each billing period unless cancelled at least 24 hours before renewal"
          ]
        },
        {
          subtitle: "3.2 In-App Purchases on iOS and Android",
          items: [
            "Purchases made in the mobile app are processed by Apple (iOS) or Google (Android) under their billing policies",
            "Refunds for mobile in-app purchases are handled by Apple or Google, not by Shot Clock",
            <>Apple refunds: <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer" className={linkCls}>reportaproblem.apple.com</a></>,
            <>Google refunds: <a href="https://play.google.com/store/account" target="_blank" rel="noopener noreferrer" className={linkCls}>play.google.com/store/account</a></>
          ]
        },
        {
          subtitle: "3.3 Web Purchases",
          items: [
            "Purchases made on our website are processed by Stripe",
            "Web subscriptions can be cancelled anytime from Settings → Subscription",
            "We offer refunds within 7 days of purchase for unused subscriptions at our discretion"
          ]
        },
        {
          subtitle: "3.4 Credits",
          items: [
            "Shot Clock Credits unlock premium features (boosts, super icebreakers, etc.) without a subscription",
            "Credits are a limited license to use the Service, not real money",
            "Credits have no cash value and are non-refundable once used",
            "Credits expire 12 months after purchase and cannot be transferred between accounts"
          ]
        },
        {
          subtitle: "3.5 Price Changes",
          items: [
            "We may change prices with at least 30 days' notice via in-app notification or email",
            "Your current subscription cycle is not affected; price changes take effect at your next renewal"
          ]
        }
      ]
    },
    {
      id: "content-conduct",
      title: "4. Content & Conduct",
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          subtitle: "4.1 Your Content",
          items: [
            "You retain ownership of the photos, text, messages, and other content you post (\"Your Content\")",
            "By posting, you grant Shot Clock a worldwide, non-exclusive, royalty-free license to host, display, reproduce, adapt, and distribute Your Content solely to operate and promote the Service"
          ]
        },
        {
          subtitle: "4.2 You Represent and Warrant That",
          items: [
            "You own or have the right to post Your Content",
            "Your Content does not infringe anyone's rights (copyright, publicity, privacy, trademark)",
            "Your Content is truthful and does not misrepresent your identity, age, or appearance",
            "Your photos are of you (no catfishing, stock photos, deceased people, or celebrities)"
          ]
        },
        {
          subtitle: "4.3 Prohibited Content & Behavior",
          items: [
            "Posting nudity, sexual content, or sexually suggestive material",
            "Harassing, bullying, threatening, stalking, or defaming anyone",
            "Hate speech, discriminatory content, or incitements to violence",
            "Impersonating anyone or misrepresenting your identity or age",
            "Commercial solicitation (sex work, financial scams, pyramid schemes, MLM, webcam promotion, etc.)",
            "Requesting or sending money, cryptocurrency, or gift cards",
            "Sharing phone numbers, home addresses, financial details, or external links in your profile",
            "Promoting drugs, weapons, violence, self-harm, or illegal activity",
            "Scraping, copying, or redistributing other users' information or photos",
            "Reverse engineering, decompiling, or circumventing Service security measures",
            "Using bots, automation, or scripts, or creating multiple accounts",
            <>Full details are in our <a href="/community-guidelines" className={linkCls} data-testid="terms-community-guidelines-link">Community Guidelines</a>, which are part of these Terms</>
          ]
        },
        {
          subtitle: "4.4 Content Moderation & 4.5 Reporting",
          items: [
            "We use automated systems and human review to detect prohibited content",
            "We may remove content, shadow-ban, suspend, or terminate accounts, and report illegal content to law enforcement",
            "Moderation is best-effort; we do not pre-screen all content and are not liable for user-generated content",
            "Tap \"Report\" on a profile or message thread to flag a violation. We review reports within 24 hours"
          ]
        }
      ]
    },
    {
      id: "safety",
      title: "5. Safety Notice & Your Responsibility",
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          subtitle: "Important",
          items: [
            "Shot Clock does not conduct criminal background checks or identity verification beyond photo verification",
            "You are solely responsible for your interactions with other users, online and in person",
            "Shot Clock cannot guarantee the identity, honesty, or safety of any user; interactions carry inherent risk"
          ]
        },
        {
          subtitle: "Safety Guidelines",
          items: [
            "Chat on the Service before sharing personal contact information",
            "Meet in public places for the first few dates",
            "Tell a friend where you're going and when you'll be back",
            "Trust your instincts — leave if you feel unsafe",
            "Never send money or financial information to someone you met on Shot Clock",
            "Report any suspicious or threatening behavior"
          ]
        }
      ]
    },
    {
      id: "intellectual-property",
      title: "6. Intellectual Property",
      icon: <Copyright className="w-5 h-5" />,
      content: [
        {
          subtitle: "6.1 Our IP",
          items: [
            "The Service, including software, designs, logos, trademarks, and text (excluding Your Content), is owned by Shot Clock Dating or its licensors",
            "\"Shot Clock,\" \"Shot Clock Dating,\" the heart-clock logo, and related marks are trademarks of Shot Clock Dating"
          ]
        },
        {
          subtitle: "6.2 Limited License",
          items: [
            "We grant you a personal, non-exclusive, non-transferable, revocable license to use the Service for private, non-commercial use",
            "You may not modify, adapt, hack, create derivative works of, resell, or sublicense the Service"
          ]
        },
        {
          subtitle: "6.3 DMCA / Copyright Complaints",
          items: [
            <>Email our DMCA agent at <a href="mailto:dmca@shotclockdating.com" className={linkCls}>dmca@shotclockdating.com</a> with your contact information, the copyrighted work, the infringing material (URL or screenshot), and a statement under penalty of perjury that you are the owner or authorized agent</>
          ]
        }
      ]
    },
    {
      id: "disclaimers",
      title: "7. Disclaimers",
      icon: <AlertTriangle className="w-5 h-5" />,
      content: [
        {
          subtitle: "\"As Is\" Service",
          items: [
            "The Service is provided \"AS IS\" and \"AS AVAILABLE\" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, non-infringement, or availability",
            "We do not warrant that the Service will be uninterrupted, timely, secure, or error-free, or that defects will be corrected",
            "We do not warrant that matches will result in a relationship or that users are who they claim to be"
          ]
        }
      ]
    },
    {
      id: "liability",
      title: "8. Limitation of Liability & 9. Indemnification",
      icon: <Scale className="w-5 h-5" />,
      content: [
        {
          subtitle: "Limitation of Liability",
          items: [
            "TO THE MAXIMUM EXTENT PERMITTED BY LAW, Shot Clock Dating and its officers, directors, employees, and agents are not liable for indirect, incidental, special, consequential, punitive, or exemplary damages",
            "This includes losses arising from your use of the Service, the conduct of any other user or third party, unauthorized access to your data, or content obtained through the Service",
            "Our total liability is limited to the greater of (a) the amount you paid us in the 12 months before the claim, or (b) US $100",
            "Some jurisdictions do not allow these limitations; they may not apply to you"
          ]
        },
        {
          subtitle: "Indemnification",
          items: [
            "You agree to defend, indemnify, and hold harmless Shot Clock Dating and its affiliates from any claim, liability, damage, loss, or expense (including attorneys' fees) arising from your violation of these Terms, your violation of third-party rights, Your Content, or your interactions with other users"
          ]
        }
      ]
    },
    {
      id: "dispute-resolution",
      title: "10. Binding Arbitration & Class-Action Waiver",
      icon: <Gavel className="w-5 h-5" />,
      content: [
        {
          subtitle: "10.1 Mandatory Arbitration",
          items: [
            "Any dispute arising from these Terms or the Service will be resolved by final, binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, in Delaware, USA",
            "Either party may bring a claim in small-claims court"
          ]
        },
        {
          subtitle: "10.2 Class-Action Waiver",
          items: [
            "You and Shot Clock waive the right to a jury trial and to participate in a class action, class arbitration, or representative action. All disputes must be brought individually"
          ]
        },
        {
          subtitle: "10.3 Opt-Out & 10.4 Exceptions",
          items: [
            <>You may opt out within 30 days of first using the Service by emailing <a href="mailto:legal@shotclockdating.com" className={linkCls}>legal@shotclockdating.com</a> with your name, email, and a statement that you opt out</>,
            "This section does not apply to claims for injunctive relief to protect intellectual property or trade secrets"
          ]
        }
      ]
    },
    {
      id: "mobile-app-terms",
      title: "13. Mobile App Terms (iOS & Android)",
      icon: <Smartphone className="w-5 h-5" />,
      content: [
        {
          subtitle: "13.1 Apple App Store (iOS)",
          items: [
            "These Terms are between you and Shot Clock Dating, not Apple",
            "Apple has no obligation to provide support or maintenance for the app",
            "Apple is not responsible for product warranties, claims, or intellectual property disputes",
            "Apple and its subsidiaries are third-party beneficiaries of these Terms with the right to enforce them against you",
            "You will comply with the Apple Media Services Terms and all applicable App Store rules"
          ]
        },
        {
          subtitle: "13.2 Google Play Store (Android)",
          items: [
            "If you downloaded the app from Google Play, you agree to comply with Google Play's Terms of Service in addition to these Terms"
          ]
        }
      ]
    },
    {
      id: "general-terms",
      title: "14. General Terms",
      icon: <FileText className="w-5 h-5" />,
      content: [
        {
          subtitle: "Legal Basics",
          items: [
            "Severability: if any provision is unenforceable, the rest remains in effect",
            "No Waiver: our failure to enforce a provision is not a waiver",
            <>Entire Agreement: these Terms, the <a href="/privacy-policy" className={linkCls}>Privacy Policy</a>, and the <a href="/community-guidelines" className={linkCls}>Community Guidelines</a> are the complete agreement between you and Shot Clock</>,
            "Assignment: you may not assign these Terms; we may assign them to any successor",
            "Notices: we may send notices to the email on file; you may send notices to legal@shotclockdating.com"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-x-hidden" data-testid="terms-of-service-page">
      <SEOHead 
        title="Terms of Service | Shot Clock Dating"
        description="Shot Clock Dating terms of service for web, iOS, and Android: eligibility, subscriptions, in-app purchases, content rules, safety, and dispute resolution."
        keywords="terms of service, user agreement, speed dating rules, in-app purchases, subscription terms, community guidelines"
        url="https://shotclockdating.com/terms-of-service"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            className="flex items-center justify-center mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <FileText className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
          </motion.div>
          <motion.p 
            className="text-xl text-rose-100 mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            The rules for using Shot Clock Dating on web, iOS, and Android
          </motion.p>
          <motion.p 
            className="text-rose-200"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            data-testid="terms-last-updated"
          >
            Effective & Last Updated: {lastUpdated}
          </motion.p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <Gavel className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">Legal Agreement</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">
            Welcome to Shot Clock Dating. These Terms of Service ("Terms") govern your use of the Shot Clock Dating mobile application, the website at shotclockdating.com, and related services (together, the "Service") operated by Shot Clock Dating ("Shot Clock," "we," "us," or "our").
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
          </p>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4" data-testid="terms-age-notice">
            <p className="text-rose-800 font-semibold">
              This Service is only for adults 18 years of age or older. By using the Service, you confirm you are at least 18.
            </p>
          </div>
        </motion.div>

        {/* Main Sections */}
        {sections.map((section, index) => (
          <motion.div 
            key={section.id}
            id={section.id}
            data-testid={`terms-section-${section.id}`}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 + (index * 0.05), duration: 0.8 }}
          >
            <div className="flex items-center mb-6">
              <div className="text-rose-600 mr-3">{section.icon}</div>
              <h2 className="text-2xl font-bold text-gray-800">{section.title}</h2>
            </div>
            
            {section.content.map((subsection, subIndex) => (
              <div key={subIndex} className="mb-6 last:mb-0">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">{subsection.subtitle}</h3>
                <ul className="space-y-2">
                  {subsection.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-rose-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>
        ))}

        {/* Contact Information */}
        <motion.div 
          className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-xl p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.8 }}
        >
          <div className="flex items-center mb-6">
            <MessageCircle className="w-6 h-6 text-rose-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">12. Changes & 15. Contact</h2>
          </div>
          <p className="text-gray-700 mb-6">
            We may update these Terms at any time. For material changes, we will notify you by email or in-app notification at least 7 days before they take effect. Continued use after the effective date means you accept the updated Terms.
          </p>
          <div className="bg-white rounded-lg p-4 border border-rose-200" data-testid="terms-contact-box">
            <p className="text-gray-700 font-semibold mb-2">Shot Clock Dating — Legal Department</p>
            <p className="text-gray-700">
              <strong>Legal:</strong>{' '}
              <a href="mailto:legal@shotclockdating.com" className={linkCls} data-testid="terms-legal-email-link">legal@shotclockdating.com</a>
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Support:</strong>{' '}
              <a href="mailto:support@shotclockdating.com" className={linkCls}>support@shotclockdating.com</a>
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Website:</strong> https://shotclockdating.com
            </p>
          </div>
        </motion.div>

        {/* Governing Law */}
        <motion.div 
          className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.8 }}
        >
          <div className="flex items-start">
            <Gavel className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">11. Governing Law</h3>
              <p className="text-blue-700 text-sm leading-relaxed" data-testid="terms-governing-law">
                These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict of laws principles.
                By clicking "Create Account" or using the Service, you acknowledge you have read, understood, and agree to these Terms, the Privacy Policy, and the Community Guidelines.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
